import { useEffect, useState, startTransition } from 'react';
import {
  TextField,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Link,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  Language,
  GppGood,
  Sensors
} from '@mui/icons-material';
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from './components/SnackbarService';
import clientService, { clearAuthTokens, clientServiceV2, saveAuthTokens } from '../clientService';
import { LoginRequest, UserRole, UserSignup } from '../api/Api';
import { useNavigate } from 'react-router';
import { useAppState } from 'src/state-management/state-provider';
import logoImgWhite from 'src/resources/images/logo-white 1.png';
import RotatingEarth from './components/RotatingEarth';
import { getPasswordPolicyError, getPasswordStrength, PASSWORD_POLICY_TEXT } from 'src/util/password-policy';
import { isPersonalEmailDomain, WORK_EMAIL_POLICY_TEXT } from 'src/util/email-policy';

const AuthPage = () => {
  const navigate = useNavigate();
  const { dispatch } = useAppState();
  const { showSnackbar, snackbarType } = useSnackbar();

  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isPasswordResetting, setIsPasswordResetting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const inputStyles = {
    mt: 0.2,
    mb: 0.5,
    '& .MuiFilledInput-root': {
      bgcolor: '#0a1410',
      color: '#FFFFFF',
      borderRadius: '8px',
      border: '1px solid rgba(0, 242, 150, 0.1)',
      '&:before, &:after': { display: 'none' },
      '&:hover': { bgcolor: 'rgba(0, 242, 150, 0.05)' },
      '&.Mui-focused': { bgcolor: '#0a1410', borderColor: '#00f296' },
      '& input': { py: 1.1 },
    },
    '& .MuiFormHelperText-root': { color: '#FF5252' },
  };

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    termsAgree: false,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const email_verification_token = searchParams.get("token");
  const signupPasswordError = activeTab === 1 && formData.password ? getPasswordPolicyError(formData.password) : '';
  const signupPasswordStrength = getPasswordStrength(formData.password);
  const signupPasswordHelperText =
    activeTab !== 1
      ? ''
      : signupPasswordError ||
      (formData.password
        ? `${signupPasswordStrength.label}. Password meets all requirements.`
        : PASSWORD_POLICY_TEXT);
  const isSignupPasswordValid = activeTab === 1 && !!formData.password && !signupPasswordError;

  // Email validation helper
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z0-9-]+$/;
    return emailRegex.test(email);
  };

  // Rehydrate resend cooldown from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('resendCooldownUntil');
    if (stored) {
      const remaining = Math.ceil((Number(stored) - Date.now()) / 1000);
      if (remaining > 0) {
        setCooldownSeconds(remaining);
      }
    }
  }, []);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const interval = setInterval(() => {
      setCooldownSeconds(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          localStorage.removeItem('resendCooldownUntil');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldownSeconds]);

  useEffect(() => {
    if (email_verification_token) {
      // Pre-fill email from token payload if possible so user doesn't have to re-type
      try {
        const parts = email_verification_token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (payload?.sub) {
            setFormData((prev) => ({ ...prev, email: payload.sub }));
          }
        }
      } catch {
        // Ignore parsing errors
      }

      const verifyEmail = async () => {
        setIsSubmitting(true);
        try {
          await clientServiceV2.api.verifyEmailAfterSignupApiV2AuthVerifyEmailPostSignupPost({ token: email_verification_token });
          showSnackbar({
            message: 'Email verified successfully! You can now log in.',
            type: snackbarType.SUCCESS,
          });
          setActiveTab(0);
        } catch (error: any) {
          const rawMsg =
            error.error?.message ||
            error.error?.detail ||
            error.response?.data?.message ||
            error.response?.data?.detail ||
            '';
          const msg =
            rawMsg.toLowerCase().includes('expired') || rawMsg.toLowerCase().includes('signature')
              ? 'Verification link has expired.'
              : rawMsg || 'Verification link has expired.';
          showSnackbar({ message: msg, type: snackbarType.ERROR });
          setShowResendEmail(true);
          setActiveTab(0);
        } finally { setIsSubmitting(false); }
      };
      verifyEmail();
    }
    clearAuthTokens();
    startTransition(() => {
      dispatch({ type: 'LOGOUT' });
    });
  }, []);

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    if (name === "email") {
      // Only show error if field has been touched and is invalid
      setEmailError(
        value && !validateEmail(value)
          ? "Invalid email address"
          : activeTab === 1 && isPersonalEmailDomain(value)
            ? WORK_EMAIL_POLICY_TEXT
            : ""
      );
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleLogin = async () => {
    const loginPayload: LoginRequest = {
      email: formData.email,
      password: formData.password,
    };
    setIsSubmitting(true);
    try {
      const response = await clientService.api.loginApiV1AuthLoginPost(loginPayload);
      saveAuthTokens(response.data);
      const userResponse = await clientService.api.getUserApiV1AuthUserGet();
      const rest = {
        ...userResponse.data,
        role: userResponse.data.role || [],
        status: (userResponse.data.status as any) || 'Active',
        is_email_verified: userResponse.data.is_email_verified || false,
      };
      startTransition(() => {
        dispatch({ type: 'SET_USER_DETAILS', payload: rest });
      });
      showSnackbar({ message: 'Login successful!' });
      if (rest.role.includes(UserRole.Admin) || rest.role.includes(UserRole.Employee)) {
        startTransition(() => {
          navigate('/admin');
        });
      } else {
        startTransition(() => {
          navigate('/landing');
        });
      }
    } catch (error: any) {
      const rawDetail =
        error?.error?.message ||
        error?.error?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.data?.message ||
        error?.data?.detail ||
        error?.message ||
        error?.detail;
      const errorMessage = typeof rawDetail === 'string'
        ? rawDetail
        : Array.isArray(rawDetail) && rawDetail[0]?.msg
          ? rawDetail[0].msg
          : typeof rawDetail === 'object' && rawDetail !== null
            ? JSON.stringify(rawDetail)
            : 'Login failed. Please try again.';

      if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('not verified')) {
        setShowResendEmail(true);
      }
      showSnackbar({
        message: errorMessage,
        type: snackbarType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = async () => {
    setIsSubmitting(true);
    try {
      const signupPayload: UserSignup = {
        email: formData.email,
        password: formData.password,
        first_name: formData.firstname,
        last_name: formData.lastname,
        tnc_checked: formData.termsAgree,
      };
      const response = await clientService.api.signupApiV1AuthSignupPost(signupPayload);

      if (response.data.access_token) {
        saveAuthTokens(response.data);
        const userResponse = await clientService.api.getUserApiV1AuthUserGet();
        const rest = {
          ...userResponse.data,
          role: userResponse.data.role || [],
          status: (userResponse.data.status as any) || 'Active',
          is_email_verified: userResponse.data.is_email_verified || false,
        };
        startTransition(() => {
          dispatch({ type: 'SET_USER_DETAILS', payload: rest });
        });
        showSnackbar({ message: 'Sign-up successful! You are now logged in.' });
        if (rest.role.includes(UserRole.User) || rest.role.includes(UserRole.Employee)) {
          startTransition(() => {
            navigate('/landing');
          });
        } else {
          startTransition(() => {
            navigate('/admin');
          });
        }
      } else {
        showSnackbar({
          message: 'Sign-up successful! Verification email sent. Please check your inbox.',
          type: snackbarType.SUCCESS,
        });
        setShowResendEmail(false);
        setActiveTab(0);
      }
    } catch (error: any) {
      const errorMessage = error.error?.detail || error.error?.message || 'Signup failed. Please try again.';
      showSnackbar({
        message: errorMessage,
        type: snackbarType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    if (cooldownSeconds > 0) return;

    // Validate email before resending
    if (!formData.email || !validateEmail(formData.email)) {
      setEmailError(!formData.email ? "Email is required" : "Invalid email address");
      showSnackbar({
        message: 'Please enter a valid email address.',
        type: snackbarType.WARNING,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await clientService.api.sendVerificationEmailAfterSignupApiV1AuthSendVerificationEmailPostSignupPost({ email: formData.email });
      showSnackbar({
        message: 'Verification email resent! Please check your inbox.',
        type: snackbarType.SUCCESS,
      });
      // Start 10-second cooldown and persist it across page refreshes
      const cooldownDuration = 30;
      const expiresAt = Date.now() + cooldownDuration * 1000;
      localStorage.setItem('resendCooldownUntil', String(expiresAt));
      setCooldownSeconds(cooldownDuration);
    } catch (error: any) {
      const errorMessage = error.error?.detail || error.error?.message || 'Failed to resend verification email. Please try again.';
      showSnackbar({
        message: errorMessage,
        type: snackbarType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validate email format
    if (!formData.email || !validateEmail(formData.email)) {
      setEmailError(!formData.email ? "Email is required" : "Invalid email address");
      showSnackbar({
        message: 'Please enter a valid email address.',
        type: snackbarType.WARNING,
      });
      return;
    }

    if (activeTab === 1 && isPersonalEmailDomain(formData.email)) {
      setEmailError(WORK_EMAIL_POLICY_TEXT);
      showSnackbar({
        message: WORK_EMAIL_POLICY_TEXT,
        type: snackbarType.WARNING,
      });
      return;
    }

    if (!formData.password) {
      showSnackbar({
        message: 'Password is required.',
        type: snackbarType.WARNING,
      });
      return;
    }

    if (activeTab === 1) {
      const passwordError = getPasswordPolicyError(formData.password);
      if (passwordError) {
        showSnackbar({
          message: PASSWORD_POLICY_TEXT,
          type: snackbarType.WARNING,
        });
        return;
      }
    }

    if (activeTab === 1 && (!formData.firstname || !formData.lastname || !formData.termsAgree)) {
      showSnackbar({
        message: 'For signup, First Name, Last Name and agreement to Terms & Conditions are required.',
        type: snackbarType.WARNING,
      });
      return;
    }

    if (activeTab === 0) {
      handleLogin();
    } else {
      handleSignup();
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        bgcolor: '#040b08',
        color: '#FFFFFF',
        fontFamily: '"Inter", "Roboto", sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Left Section - Form */}
      <Box
        sx={{
          flex: { xs: 1, md: 0.8 },
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 3, md: 4 },
          maxWidth: { md: '600px' },
        }}
      >
        <Box sx={{ mb: { xs: 1, md: 2 } }}>
          <img
            src={logoImgWhite}
            alt="Darukaa Earth Logo"
            style={{ maxWidth: '140px', height: 'auto' }}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {!isForgotPasswordMode ? (
            <>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {activeTab === 0 ? 'Welcome back' : 'Create account'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#9BA1A6', mb: 1 }}>
                {activeTab === 0
                  ? 'Sign in to access your environmental intelligence platform.'
                  : 'Join us to help build a resilient future for planetary resources.'}
              </Typography>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                {activeTab === 1 && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9BA1A6', ml: 1 }}>
                        FIRST NAME
                      </Typography>
                      <TextField
                        fullWidth
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleInputChange}
                        variant="filled"
                        placeholder="John"
                        disabled={isSubmitting}
                        required
                        sx={inputStyles}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9BA1A6', ml: 1 }}>
                        LAST NAME
                      </Typography>
                      <TextField
                        fullWidth
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleInputChange}
                        variant="filled"
                        placeholder="Doe"
                        disabled={isSubmitting}
                        required
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#9BA1A6', ml: 1 }}>
                    EMAIL ADDRESS
                  </Typography>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    variant="filled"
                    placeholder="name@natureanalytics.earth"
                    disabled={isSubmitting}
                    required
                    error={!!emailError}
                    helperText={emailError}
                    sx={inputStyles}
                  />
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#9BA1A6', ml: 1 }}>
                    PASSWORD
                  </Typography>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    variant="filled"
                    placeholder="••••••••"
                    disabled={isSubmitting}
                    required
                    error={!!signupPasswordError}
                    helperText={signupPasswordHelperText}
                    sx={{
                      ...inputStyles,
                      '& .MuiFormHelperText-root': {
                        color: isSignupPasswordValid ? '#00f296' : '#FF5252',
                      },
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={isSubmitting}
                              sx={{ color: '#9BA1A6' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  {activeTab === 1 && formData.password && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -0.25, mb: 0.75 }}>
                      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
                        {[1, 2, 3, 4].map(step => (
                          <Box
                            key={step}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              bgcolor: step <= signupPasswordStrength.score ? '#00f296' : '#2D3139',
                            }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#9BA1A6' }}>
                        {signupPasswordStrength.label}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {activeTab === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 0.2,
                      mb: 1.25,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          sx={{
                            color: '#2D3139',
                            '&.Mui-checked': { color: '#A0D9B1' },
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ color: '#9BA1A6' }}>Remember me</Typography>}
                    />
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setIsForgotPasswordMode(true)}
                      sx={{ color: '#9BA1A6', textDecoration: 'none', '&:hover': { color: '#00f296' } }}
                    >
                      Forgot password?
                    </Link>
                  </Box>
                ) : (
                  <Box sx={{ mt: 0.25, mb: 1.25, display: 'flex', alignItems: 'flex-start' }}>
                    <Checkbox
                      name="termsAgree"
                      checked={formData.termsAgree}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      size="small"
                      sx={{
                        color: '#2D3139',
                        mt: -0.5,
                        '&.Mui-checked': { color: '#00f296' },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: '#9BA1A6' }}>
                      I agree to the{' '}
                      <Link
                        component="button"
                        type="button"
                        onClick={() => !isSubmitting && setDialogOpen(true)}
                        sx={{ color: '#FFF', textDecoration: 'underline' }}
                      >
                        Terms & Conditions
                      </Link>
                    </Typography>
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  endIcon={!isSubmitting && <ArrowForward />}
                  sx={{
                    bgcolor: '#00f296',
                    color: '#040b08',
                    fontWeight: 700,
                    py: 1.5,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: '#00d984' },
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : activeTab === 0 ? (
                    'Login'
                  ) : (
                    'Create Account'
                  )}
                </Button>

                {showResendEmail && activeTab === 0 && (
                  <Box
                    textAlign="center"
                    mt={2}
                    p={1.5}
                    sx={{
                      bgcolor: 'rgba(0, 242, 150, 0.05)',
                      border: '1px solid rgba(0, 242, 150, 0.2)',
                      borderRadius: '8px',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#9BA1A6', mb: 0.5 }}>
                      Didn't receive verification email or link expired?
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleResendEmail}
                      disabled={isSubmitting || cooldownSeconds > 0}
                      sx={{
                        color: cooldownSeconds > 0 ? '#9BA1A6' : '#00f296',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'rgba(0, 242, 150, 0.1)' },
                      }}
                    >
                      {cooldownSeconds > 0
                        ? `Resend available in ${cooldownSeconds}s`
                        : 'Resend verification email'}
                    </Button>
                  </Box>
                )}
              </form>

              <Box sx={{ mt: 3, textAlign: 'center', position: 'relative' }}>
                <Divider sx={{ bgcolor: '#2D3139' }} />
                <Typography
                  variant="body2"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: '#040b08',
                    px: 2,
                    color: '#9BA1A6',
                  }}
                >
                  or
                </Typography>
              </Box>

              <Box sx={{ mt: 2.5, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#9BA1A6' }}>
                  {activeTab === 0 ? "Don't have an account? " : "Already have an account? "}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => setActiveTab(activeTab === 0 ? 1 : 0)}
                    sx={{ color: '#00f296', fontWeight: 600, textDecoration: 'none' }}
                  >
                    {activeTab === 0 ? 'Create account' : 'Login'}
                  </Link>
                </Typography>
              </Box>
            </>
          ) : (
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                Reset Password
              </Typography>
              <Typography variant="body1" sx={{ color: '#9BA1A6', mb: 4 }}>
                Enter your registered email address to receive a password reset link.
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#9BA1A6', ml: 1 }}>
                  EMAIL ADDRESS
                </Typography>
                <TextField
                  fullWidth
                  value={resetEmail}
                  type="email"
                  onChange={(e) => {
                    const value = e.target.value;
                    setResetEmail(value);
                    setResetEmailError(value && !validateEmail(value) ? 'Invalid email address' : '');
                  }}
                  variant="filled"
                  placeholder="name@natureanalytics.earth"
                  disabled={isPasswordResetting}
                  required
                  error={!!resetEmailError}
                  helperText={resetEmailError}
                  sx={inputStyles}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={async () => {
                  if (!resetEmail || !validateEmail(resetEmail)) {
                    setResetEmailError(!resetEmail ? 'Email is required' : 'Invalid email address');
                    showSnackbar({
                      message: 'Please enter a valid email address.',
                      type: snackbarType.WARNING,
                    });
                    return;
                  }
                  setIsPasswordResetting(true);
                  try {
                    await clientServiceV2.api.requestPasswordResetApiV2RequestPasswordResetPost({
                      email: resetEmail,
                    });
                    showSnackbar({
                      message: 'If your email is registered, a password reset link has been sent.',
                      type: snackbarType.SUCCESS,
                    });
                    setIsForgotPasswordMode(false);
                    setResetEmail('');
                    setResetEmailError('');
                  } catch (error: any) {
                    const msg = error.response?.data?.detail || 'Failed to send password reset email.';
                    showSnackbar({ message: msg, type: snackbarType.ERROR });
                  } finally {
                    setIsPasswordResetting(false);
                  }
                }}
                disabled={isPasswordResetting}
                sx={{
                  mt: 4,
                  bgcolor: '#00f296',
                  color: '#040b08',
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#00d984' },
                }}
              >
                {isPasswordResetting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    setIsForgotPasswordMode(false);
                    setResetEmailError('');
                  }}
                  disabled={isPasswordResetting}
                  sx={{ color: '#9BA1A6', textDecoration: 'none' }}
                >
                  Back to Login
                </Link>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Section - Branding */}
      <Box
        sx={{
          flex: { xs: 0, md: 1.2 },
          display: { xs: 'none', md: 'flex' },
          bgcolor: '#040b08',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: '-70%',
            right: '-60%',
            width: '180%',
            height: '180%',
            zIndex: 0,
            opacity: 0.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            maskImage: 'radial-gradient(circle at 75% 75%, black 20%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(circle at 75% 75%, black 20%, transparent 80%)',
            '& canvas': {
              filter: 'blur(0.5px)',
            }
          }}
        >
          <RotatingEarth />
        </Box>

        <Box
          sx={{
            maxWidth: '800px',
            width: '100%',
            position: 'relative',
            zIndex: 1,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              mb: 6,
              fontSize: { md: '2.2rem', lg: '2.6rem' },
              lineHeight: 1.2,
              textAlign: 'center',
            }}
          >
            Transforming data into <br /> environmental intelligence
          </Typography>

          <Box sx={{ display: 'flex', gap: 6, mt: 10, justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  mx: 'auto',
                }}
              >
                <Language sx={{ color: '#00f296' }} />
              </Box>
              <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 1.2, display: 'block' }}>
                ENVIRONMENTAL<br />INTELLIGENCE
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  mx: 'auto',
                }}
              >
                <GppGood sx={{ color: '#00f296' }} />
              </Box>
              <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 1.2, display: 'block' }}>
                RISK &<br />COMPLIANCE
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  mx: 'auto',
                }}
              >
                <Sensors sx={{ color: '#00f296' }} />
              </Box>
              <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 1.2, display: 'block' }}>
                SIGNAL PROCESSING &<br />EMERGING TECH
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: '#0a1410',
            color: '#FFFFFF',
            maxHeight: '90vh',
            border: '1px solid rgba(0, 242, 150, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, borderBottom: '1px solid #2D3139', pb: 2 }}>
          Terms and Conditions
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: '#9BA1A6' }} gutterBottom>
            <strong>Effective Date:</strong> November 15, 2025<br />
            <strong>Last Updated:</strong> November 15, 2025
          </Typography>

          <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
            Welcome to <strong>Darukaa.Earth</strong>! These Terms and Conditions ("Terms") govern your use of our geospatial software-as-a-service platform for climate intelligence, carbon analysis, biodiversity assessment, and environmental monitoring ("Service"). By creating an account or using the Service, you agree to these Terms.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            1. Use of Service
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            We grant you a limited, non-exclusive license to use the Service for your business or personal purposes.
            You may not copy, resell, redistribute, reverse engineer, or misuse the Service or its data.
            You are responsible for keeping your account credentials secure and for all activities under your account.
            You must be at least 18 years old to use the Service.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            2. Data and Content
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            All platform software, datasets, algorithms, maps, and analytical content belong to Darukaa Earth or its partners.
            Any geospatial data, projects, or content you upload remains your property, but you grant us permission to process it to provide the Service.
            We may use third-party data sources, satellite imagery, and APIs subject to their respective terms.
            You represent that you have rights to any data you upload.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            3. Service Features
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            The Service includes carbon stock analysis, biodiversity assessments, habitat health monitoring, and environmental impact analysis.
            Certain advanced features, API access, or premium datasets may require a paid subscription.
            We reserve the right to modify, suspend, or discontinue features with reasonable notice.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            4. Payments
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            Paid subscriptions or one-time purchases must be settled according to the plan you select.
            Unless otherwise stated, payments are non-refundable, except as required by applicable law.
            You are responsible for all taxes applicable to your purchases.
            We may change pricing with 30 days' notice to subscribers.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            5. Service Availability
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            We aim for high availability but cannot guarantee uninterrupted access.
            Maintenance, updates, third-party service outages, or external factors may cause downtime.
            We are not liable for delays or failures due to circumstances beyond our reasonable control.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            6. Privacy
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            Your personal data and project information will be handled according to our Privacy Policy.
            We use industry-standard security measures but cannot guarantee absolute security.
            By using the Service, you consent to our data processing practices.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            7. Disclaimers
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            The Service is provided "as is" and "as available" without warranties of any kind, express or implied.
            Geospatial data, satellite imagery, and analytical results may contain inaccuracies or limitations.
            You should not rely solely on our data for decisions where precision is critical (e.g., safety, legal compliance, or financial planning).
            We do not warrant that the Service will meet your specific requirements or be error-free.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            8. Limitation of Liability
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            To the extent permitted by law, our total liability is limited to the amount you paid for the Service in the past 12 months.
            We are not responsible for indirect, consequential, incidental, or punitive damages.
            You agree to indemnify us against claims arising from your breach of these Terms or misuse of the Service.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            9. Termination
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            We may suspend or terminate your access if you breach these Terms, engage in fraud, or misuse the Service.
            You may close your account at any time through the platform or by contacting us.
            Upon termination, your access to the Service will cease, and we may delete your data in accordance with our data retention policy.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            10. Intellectual Property
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            All trademarks, logos, and service marks belong to Darukaa Earth or their respective owners.
            You may not use our branding without prior written permission.
            You retain ownership of your project data but grant us a license to process it to provide the Service.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            11. Governing Law
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            These Terms are governed by the laws of India. Any disputes must be resolved in the courts of Bombay High Court, Maharashtra, India.
            If any provision is found unenforceable, the remaining provisions will remain in effect.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            12. Changes to Terms
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            We may update these Terms to reflect changes in our Service or for legal reasons.
            Material changes will be communicated via email or through the platform.
            Continued use of the Service after changes means you accept the updated Terms.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            Contact Us
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#9BA1A6' }}>
            Questions about these Terms? Contact us at:<br />
            <strong>Email:</strong> compliance@darukaa.com<br />
            <strong>Company:</strong> Darukaa Earth<br />
            <strong>Website:</strong> https://www.darukaa.earth
          </Typography>

          <Typography variant="caption" sx={{ display: 'block', mt: 3, fontStyle: 'italic', color: '#9BA1A6' }}>
            By accepting these Terms, you acknowledge that you have read, understood, and agree to be bound by them.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #2D3139' }}>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#A0D9B1',
              color: '#0B0E11',
              fontWeight: 600,
              '&:hover': { bgcolor: '#8ec9a0' }
            }}
            fullWidth
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuthPage;
